import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { logger } from '../utils/logger';

interface Props {
  /**
   * Made children optional to ensure compatibility with various JSX parser/TypeScript configurations
   */
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * ErrorBoundary component to catch and handle UI failures gracefully.
 */
// Fix: Explicitly extending the named 'Component' import with generics to ensure 'props' and 'state' are correctly recognized by TypeScript
export class ErrorBoundary extends Component<Props, State> {
  /**
   * Fix: Initializing state as a class property to ensure it is recognized by TypeScript.
   * This resolves "Property 'state' does not exist on type 'ErrorBoundary'" errors.
   */
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.log(`Critical UI Failure: ${error.message}`, 'error', {
      componentStack: errorInfo.componentStack,
    });
  }

  private handleReset = () => {
    window.location.href = '/';
  };

  public render() {
    /**
     * Fix: Accessing state via this.state which is inherited from Component.
     */
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-beige-100 flex items-center justify-center p-6 text-center">
          <div className="max-w-md w-full glass p-12 rounded-[3rem] border-red-200 border shadow-2xl animate-in zoom-in duration-500">
            <div className="w-20 h-20 bg-red-100 text-red-600 rounded-3xl flex items-center justify-center mx-auto mb-8">
              <AlertTriangle size={40} />
            </div>
            <h1 className="text-3xl font-black text-primary-950 mb-4 tracking-tighter">
              System Interruption
            </h1>
            <p className="text-primary-800 mb-10 leading-relaxed font-medium">
              We encountered a technical anomaly in the rendering engine. Our diagnostics team (and
              AI) have been notified.
            </p>
            <div className="flex flex-col gap-4">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center justify-center gap-3 py-4 bg-primary-900 text-white rounded-2xl font-bold hover:bg-primary-800 transition-all shadow-lg"
              >
                <RefreshCcw size={18} /> Restart Session
              </button>
              <button
                onClick={this.handleReset}
                className="flex items-center justify-center gap-3 py-4 bg-white border border-primary-200 text-primary-900 rounded-2xl font-bold hover:bg-beige-200 transition-all"
              >
                <Home size={18} /> Return Home
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && (
              <pre className="mt-8 p-4 bg-black/5 rounded-xl text-[10px] text-left overflow-auto max-h-32 text-red-800">
                {/**
                 * Fix: Access error from this.state
                 */}
                {this.state.error?.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }

    /**
     * Fix: Correctly access children through this.props, which is inherited from the Component class.
     */
    return this.props.children;
  }
}
